import MapView from '@arcgis/core/views/MapView';
import type { ViewHitTestResult } from '@arcgis/core/views/types';
import EsriUtils from './utils/esriHelpers';
import {
	getVtlIconSpriteColor,
	getVtlPaintColor,
	getVtlRawIconImage,
} from './utils/rendererHelpers';
import { resolveExpression, colorFromSpriteId } from './utils/expressionEvaluator';
import type { SelectedAsset } from './types';
import type { ConfigLayer } from '@Map/panel/types';
import type { FeatureItem } from './features/AssetGrid/utils/types';

/** A hit-tested asset (full attributes/properties, same shape as getAssetAtScreenPoint) plus the screen point it was found at. */
export type EsriAssetScreenPoint = SelectedAsset & { x: number; y: number };

export interface EsriSelectionState {
	selectedAssetIds: string[];
	highlightedAssetIds: string[];
	selectedLayerId: string | null;
}

export interface EsriPropertyPaneState {
	isOpen: boolean;
	assets: FeatureItem[];
	activeIndex: number;
}

/** Live accessors backing the state-reporting test hooks — read fresh values on every call, not a stale closure. */
export interface EsriTestHooksState {
	getSelectionState: () => EsriSelectionState;
	getThemeConfig: (layerId: string) => Partial<ConfigLayer> | undefined;
	getPropertyPaneState: () => EsriPropertyPaneState;
}

/**
 * Cypress-only QA API for driving/asserting the esri canvas. Installed by
 * {@link installEsriTestHooks}, gated on `window.Cypress`; no-op in production.
 */
export interface EsriTestHooks {
	/**
	 * Hit-tests a screen point (as returned by getAssetScreenPoint/listVisibleAssets)
	 * and returns the asset(s) rendered there. Layers with no `id` field in their
	 * tile schema (e.g. tenant-uploaded spatial/`tiles-other` overlays) get a
	 * synthesized `id`/`assetId` (`"<subLayerId>#<attrs>|<geometry>"`) —
	 * a stable-enough handle to click/reference, not a real backend asset id.
	 */
	getAssetAtScreenPoint(x: number, y: number): Promise<SelectedAsset[]>;
	/**
	 * Finds a currently-rendered asset by id via a hitTest sweep of the viewport
	 * and returns its screen point for `cy.click(x, y)`. `assetId` matches either
	 * `asset.id` (raw tile id, e.g. `"pipe#Wtr-Mn-1"`) or `asset.assetId` (bare id)
	 * as surfaced by getAssetAtScreenPoint/selection callbacks. Assets rendered as
	 * VectorTileLayer tiles have no client-side geometry index — hitTest against
	 * rendered pixels is the only way to locate them (see getVtlIconSpriteColor
	 * comment history / useSelection.ts zoomToAsset). Grid-sampled, so it can miss
	 * features smaller than the sample step or fully occluded by another feature.
	 */
	getAssetScreenPoint(
		layerId: string | undefined,
		assetId: string,
	): Promise<{ x: number; y: number } | undefined>;
	/**
	 * Returns the rendered colour for a layer's symbol/line/fill style layer.
	 * Without `assetId`: the layer's category-level colour (its base style
	 * layer definition — a flat colour for fixed themes, or the raw
	 * match/step expression object for colour-by themes, not a resolved value).
	 * With `assetId`: resolves that expression against the specific asset's
	 * attributes (found via a hitTest sweep) — the actual colour QA sees
	 * rendered for THAT asset under a colour-by-attribute/range theme, so a
	 * "recolour per asset" feature can be validated per-asset, not just
	 * per-category. Returns undefined if the asset isn't currently rendered
	 * or the layer has no resolvable colour.
	 */
	getAssetColor(layerId: string, assetId?: string): Promise<unknown | undefined>;
	/**
	 * Sweeps the viewport with a grid of hitTest samples and returns the assets
	 * actually found rendered — full attributes/properties plus screen position —
	 * for logging what's on screen or grid-clicking without knowing ids upfront.
	 * Optionally scoped to one layer. Grid-sampled (see getAssetScreenPoint), so
	 * small/occluded features can be missed. Layers with no `id` field (see
	 * getAssetAtScreenPoint) collapse to ONE entry per sub-layer (first hit
	 * wins) rather than one per feature — there's no way to distinguish two
	 * same-layer features (e.g. two separate polygons) without a real id.
	 */
	listVisibleAssets(layerId?: string): Promise<EsriAssetScreenPoint[]>;
	/** Current selected/highlighted asset ids — asserts "clicking asset X selected/highlighted it", not just "clicked at (x, y)". */
	getSelectionState(): EsriSelectionState;
	/**
	 * The applied theme config (colorBy, attribute, colorRange, color) for a layer,
	 * merged from unsaved edits over the last fully-loaded theme. Asserts the theme
	 * editor's applied config matches intent, independent of the rendered pixel colour.
	 */
	getThemeConfig(layerId: string): Partial<ConfigLayer> | undefined;
	/** Current property pane open/closed state, its asset list, and the active tab/asset index. */
	getPropertyPaneState(): EsriPropertyPaneState;
}

/** Screen-space sample step, in pixels. Roughly matches typical icon size — coarser
 * misses small/adjacent features, finer costs more hitTest calls per sweep. */
const SWEEP_STEP_PX = 24;

interface SweepHit {
	asset: SelectedAsset;
	x: number;
	y: number;
}

/**
 * Grid-samples the viewport with `view.hitTest`, the only mechanism that can
 * locate individual assets rendered as VectorTileLayer tiles (no client-side
 * geometry index exists for those — see useSelection.ts's zoomToAsset comment).
 * Dedupes by asset id, keeping the first (top-left-most) hit point.
 */
async function sweepVisibleAssets(view: MapView): Promise<SweepHit[]> {
	const points: Array<{ x: number; y: number }> = [];
	for (let x = 0; x < view.width; x += SWEEP_STEP_PX) {
		for (let y = 0; y < view.height; y += SWEEP_STEP_PX) {
			points.push({ x, y });
		}
	}
	const results = await Promise.all(
		points.map(p =>
			view.hitTest(p as __esri.ScreenPoint, { include: view.map?.layers }),
		),
	);
	const byId = new Map<string, SweepHit>();
	results.forEach((result, i) => {
		for (const asset of extractHitAssets(result)) {
			if (asset.id && !byId.has(asset.id)) {
				byId.set(asset.id, { asset, ...points[i] });
			}
		}
	});
	return [...byId.values()];
}

/**
 * Fallback for graphics `EsriUtils.viewHitTestResultsToSelectedAssets` drops
 * silently: it requires a resolvable `_id`/`id`/`assetId` attribute to build a
 * SelectedAsset, but some layers (e.g. tenant-uploaded spatial/`tiles-other`
 * overlays) declare no `id` field in their tile schema at all — the graphic is
 * genuinely hit, just invisible to that helper. Synthesizes an id from the
 * *sub*-style-layer (`graphic.origin.layerId`, e.g. `Polygon1` vs `Polygon2` —
 * NOT the parent VectorTileLayer's own id, which is shared by every sub-layer
 * and would silently merge distinct layers together) plus attributes/geometry
 * if present. Esri does not populate `graphic.geometry` on VTL hitTest results
 * in practice, so for these layers attributes/geometry are typically both
 * empty and every hit on the same sub-layer collapses to ONE entry (first hit
 * wins) — there is no way to distinguish two same-layer features (e.g. two
 * separate fire-station polygons) without a real id. Skips any graphic that
 * already has a real id (already covered above).
 */
function fallbackGraphicAssets(result: ViewHitTestResult): SelectedAsset[] {
	const out: SelectedAsset[] = [];
	for (const r of result.results ?? []) {
		if (r.type !== 'graphic') continue;
		const graphic = (
			r as {
				graphic?: {
					attributes?: Record<string, unknown>;
					geometry?: unknown;
					origin?: { layerId?: string };
				};
			}
		).graphic;
		const attributes = graphic?.attributes ?? {};
		if (attributes._id ?? attributes.id ?? attributes.assetId) continue;
		const layerId =
			graphic?.origin?.layerId ?? (r as { layer?: { id?: string } }).layer?.id;
		if (!layerId) continue;
		const signature = `${JSON.stringify(attributes)}|${JSON.stringify(graphic?.geometry ?? null)}`;
		const id = `${layerId}#${signature}`;
		out.push({
			...attributes,
			id,
			assetId: id,
			layerId,
			assetType: 'amSpatial',
		} as unknown as SelectedAsset);
	}
	return out;
}

/** Combines the standard hit-test extraction with the id-less-graphic fallback above. */
function extractHitAssets(result: ViewHitTestResult): SelectedAsset[] {
	return [
		...EsriUtils.viewHitTestResultsToSelectedAssets(result),
		...fallbackGraphicAssets(result),
	];
}

function matchesAsset(
	hit: SweepHit,
	assetId: string,
	layerId?: string,
): boolean {
	if (layerId && hit.asset.layerId !== layerId) return false;
	return hit.asset.id === assetId || hit.asset.assetId === assetId;
}

/**
 * Test hook: installs `window.esriTestHooks`, a QA-facing API for canvas
 * automation (click coordinates, colour assertions) that Cypress specs can't
 * get from raw `window.esriMapView`/`window.esriMap` alone, since assets are
 * rendered as VectorTileLayer tiles rather than individually queryable
 * FeatureLayer graphics. No-op outside Cypress.
 */
export function installEsriTestHooks(
	view: MapView,
	state: EsriTestHooksState,
): void {
	if (typeof window === 'undefined' || !window.Cypress) return;

	window.esriTestHooks = {
		async getAssetAtScreenPoint(x, y) {
			const result = await view.hitTest(
				{ x, y } as __esri.ScreenPoint,
				{ include: view.map?.layers },
			);
			return extractHitAssets(result);
		},
		async getAssetScreenPoint(layerId, assetId) {
			const hits = await sweepVisibleAssets(view);
			const hit = hits.find(h => matchesAsset(h, assetId, layerId));
			return hit ? { x: hit.x, y: hit.y } : undefined;
		},
		async getAssetColor(layerId, assetId) {
			if (!assetId) {
				const iconImage = getVtlRawIconImage(view, layerId);
				return colorFromSpriteId(iconImage) ?? getVtlIconSpriteColor(view, layerId) ??
					getVtlPaintColor(view, layerId);
			}
			const hits = await sweepVisibleAssets(view);
			const hit = hits.find(h => matchesAsset(h, assetId, layerId));
			if (!hit) return undefined;
			const properties = hit.asset as unknown as Record<string, unknown>;
			const iconImage = getVtlRawIconImage(view, layerId);
			if (iconImage !== undefined) {
				const resolved = resolveExpression(iconImage, properties);
				return colorFromSpriteId(resolved);
			}
			const paintColor = getVtlPaintColor(view, layerId);
			return paintColor !== undefined
				? resolveExpression(paintColor, properties)
				: undefined;
		},
		async listVisibleAssets(layerId) {
			const hits = await sweepVisibleAssets(view);
			return hits
				.filter(h => !layerId || h.asset.layerId === layerId)
				.map(h => ({ ...h.asset, x: h.x, y: h.y }));
		},
		getSelectionState() {
			return state.getSelectionState();
		},
		getThemeConfig(layerId) {
			return state.getThemeConfig(layerId);
		},
		getPropertyPaneState() {
			return state.getPropertyPaneState();
		},
	};
}

/** Test hook: clears `window.esriTestHooks`. Call on unmount. No-op outside Cypress. */
export function uninstallEsriTestHooks(): void {
	if (typeof window === 'undefined' || !window.Cypress) return;
	delete window.esriTestHooks;
}
